import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCookieSync } from '../contexts/CookieSyncContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import SEO from '../components/SEO';
import { ArrowLeft, Clock, Eye, Sparkles, Share2, Bookmark, BookmarkCheck, Play, Heart, Star, BookOpen, Video, Lightbulb, TrendingUp, SearchX, Home, ArrowRight, Brain, HelpCircle, ShoppingBag, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get SEO-friendly content URL
const getContentUrl = (item) => `/content/${item.slug || item.content_id}`;

export default function ContentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { trackContentView } = useCookieSync();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [pointsEarned, setPointsEarned] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [bookmarked, setBookmarked] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState(0);
  const [userRating, setUserRating] = useState(null);
  const [avgRating, setAvgRating] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);
  const [likeLoading, setLikeLoading] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [knowledgeTips, setKnowledgeTips] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [moreArticles, setMoreArticles] = useState([]);
  const pointsAwardedRef = useRef(false);
  const trackingRef = useRef(false);

  // Safe localStorage access for mobile compatibility
  let token = null;
  try {
    token = localStorage.getItem('skinuse_token');
  } catch (e) {
    // localStorage not available
  }
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  // Fetch recommendations
  useEffect(() => {
    if (id) {
      fetch(`${BACKEND_URL}/api/content/${id}/recommendations?limit=4`)
        .then(res => res.ok ? res.json() : [])
        .then(data => setRecommendations(data))
        .catch(() => setRecommendations([]));
    }
  }, [id]);

  // Fetch related content, videos, tips, and products
  useEffect(() => {
    if (id) {
      // Related content
      fetch(`${BACKEND_URL}/api/content/${id}/related?limit=6`)
        .then(res => res.ok ? res.json() : [])
        .then(data => setMoreArticles(data))
        .catch(() => setMoreArticles([]));
      
      // Related videos
      fetch(`${BACKEND_URL}/api/videos/related?limit=4`)
        .then(res => res.ok ? res.json() : [])
        .then(data => setRelatedVideos(data))
        .catch(() => setRelatedVideos([]));
      
      // Knowledge tips
      fetch(`${BACKEND_URL}/api/tips/random?count=2`)
        .then(res => res.ok ? res.json() : [])
        .then(data => setKnowledgeTips(data))
        .catch(() => setKnowledgeTips([]));
      
      // Featured products
      fetch(`${BACKEND_URL}/api/products/featured`)
        .then(res => res.ok ? res.json() : [])
        .then(data => setFeaturedProducts(data.slice(0, 4)))
        .catch(() => setFeaturedProducts([]));
    }
  }, [id]);

  const getContentIcon = (type) => {
    switch (type) {
      case 'video': return Video;
      case 'knowledge': return Lightbulb;
      case 'article': return BookOpen;
      default: return TrendingUp;
    }
  };

  useEffect(() => {
    // Reset state when content ID changes
    pointsAwardedRef.current = false;
    trackingRef.current = false;
    setPointsEarned(false);
    setLoading(true);
    setContent(null);
    setNotFound(false);
    setSuggestions([]);
    setLiked(false);
    setBookmarked(false);
    setUserRating(null);
    
    fetch(`${BACKEND_URL}/api/content/${id}`, { headers })
      .then(res => {
        if (res.status === 404) {
          // Content not found - fetch suggestions
          setNotFound(true);
          return fetch(`${BACKEND_URL}/api/content/suggestions/popular?limit=6`)
            .then(sugRes => sugRes.json())
            .then(sugData => {
              setSuggestions(sugData || []);
              return null;
            });
        }
        if (!res.ok) {
          throw new Error('Content not found');
        }
        return res.json();
      })
      .then(data => {
        if (!data) return; // 404 case handled above
        
        // Check for 301 redirect - content was accessed via old content_id
        if (data._redirect_to) {
          // Perform SEO-friendly redirect to slug URL
          navigate(data._redirect_to, { replace: true });
          return;
        }
        
        // Verify this is actual content, not an error object
        if (data && data.content_id && data.title) {
          setContent(data);
          setLiked(data.user_liked || false);
          setLikeCount(data.likes || 0);
          setBookmarked(data.user_bookmarked || false);
          setBookmarkCount(data.bookmark_count || 0);
          setUserRating(data.user_rating || null);
          setAvgRating(data.rating_count > 0 ? (data.rating_sum / data.rating_count).toFixed(1) : 0);
          setRatingCount(data.rating_count || 0);
          
          // Track content view for Cookie-Sync personalization
          if (!trackingRef.current) {
            trackContentView(data);
            trackingRef.current = true;
          }
          // Award points for reading - only once per session
          if (user && !pointsAwardedRef.current) {
            earnPoints(data.points_reward || 10);
            pointsAwardedRef.current = true;
          }
        } else {
          console.error('Invalid content data:', data);
          setNotFound(true);
          // Fetch suggestions for invalid content
          fetch(`${BACKEND_URL}/api/content/suggestions/popular?limit=6`)
            .then(res => res.json())
            .then(sugData => setSuggestions(sugData || []));
        }
      })
      .catch(err => {
        console.error('Content fetch error:', err);
        setNotFound(true);
        // Fetch suggestions on error
        fetch(`${BACKEND_URL}/api/content/suggestions/popular?limit=6`)
          .then(res => res.json())
          .then(sugData => setSuggestions(sugData || []));
      })
      .finally(() => setLoading(false));
  }, [id, navigate]); // Include navigate in dependencies

  const handleLike = async () => {
    if (!user) {
      toast.error('Please sign in to like content');
      return;
    }
    if (likeLoading) return;
    
    setLikeLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/content/${id}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLiked(data.liked);
        setLikeCount(data.likes);
        toast.success(data.liked ? 'Added to your likes!' : 'Removed from likes');
      }
    } catch (error) {
      console.error('Like error:', error);
      toast.error('Could not update like');
    } finally {
      setLikeLoading(false);
    }
  };

  const handleBookmark = async () => {
    if (!user) {
      toast.error('Please sign in to bookmark content');
      return;
    }
    if (bookmarkLoading) return;
    
    setBookmarkLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/content/${id}/bookmark`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers }
      });
      
      if (res.ok) {
        const data = await res.json();
        setBookmarked(data.bookmarked);
        setBookmarkCount(data.bookmark_count);
        toast.success(data.bookmarked ? 'Saved to bookmarks!' : 'Removed from bookmarks');
      }
    } catch (error) {
      console.error('Bookmark error:', error);
      toast.error('Could not update bookmark');
    } finally {
      setBookmarkLoading(false);
    }
  };

  const handleRate = async (rating) => {
    if (!user) {
      toast.error('Please sign in to rate content');
      return;
    }
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/content/${id}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ rating })
      });
      
      if (res.ok) {
        const data = await res.json();
        setUserRating(data.user_rating);
        setAvgRating(data.avg_rating);
        setRatingCount(data.rating_count);
        toast.success(`Rated ${rating} stars!`);
      }
    } catch (error) {
      console.error('Rating error:', error);
      toast.error('Could not submit rating');
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = content?.title || 'Check out this article on SkinUse';
    const shareText = content?.summary || 'Great skincare content from SkinUse';
    
    // Try native share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
        toast.success('Shared successfully!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Fallback to clipboard
          copyToClipboard(shareUrl);
        }
      }
    } else {
      // Fallback for desktop - copy to clipboard
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied to clipboard!');
    }
  };

  const earnPoints = async (points) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/add-points`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ points, reason: 'content_read', content_id: id })
      });
      if (res.ok) {
        const data = await res.json();
        updateUser({ skin_points: data.skin_points });
        setPointsEarned(true);
        toast.success(`+${points} SkinPoints earned!`);
      }
    } catch (error) {
      console.error('Points error:', error);
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'news': return 'Daily Drop';
      case 'article': return 'Deep Dive';
      case 'video': return 'Video';
      case 'knowledge': return 'Knowledge';
      default: return 'Content';
    }
  };

  const getBackLink = (type) => {
    switch (type) {
      case 'news': return '/daily-drop';
      case 'article': return '/deep-dives';
      case 'video': return '/video-vault';
      case 'knowledge': return '/knowledge';
      default: return '/';
    }
  };

  // Set dynamic page title
  useEffect(() => {
    if (content?.title) {
      document.title = `${content.title} | SkinUse`;
    }
    return () => { document.title = 'SkinUse | Your Intelligent Skin & Hair Care Companion'; };
  }, [content?.title]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Skeleton className="h-8 w-32 mb-8" />
            <Skeleton className="aspect-video mb-8" />
            <Skeleton className="h-12 w-full mb-4" />
            <Skeleton className="h-4 w-48 mb-8" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // 404 Content Not Found with Suggestions
  if (notFound || !content) {
    return (
      <div className="min-h-screen flex flex-col bg-background" data-testid="content-not-found">
        <SEO 
          title="Content Not Found"
          description="The content you're looking for doesn't exist. Explore our popular skincare and haircare content instead."
          robots="noindex, nofollow"
        />
        <Navbar />
        <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* 404 Hero */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/10 mb-6">
                <SearchX className="w-10 h-10 text-gold" />
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl font-semibold mb-4">
                Content Not Found
              </h1>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                This content may have been moved or no longer exists. 
                Check out some of our popular content instead.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/">
                  <Button className="btn-gold gap-2" data-testid="go-home-btn">
                    <Home className="w-4 h-4" />
                    Go Home
                  </Button>
                </Link>
                <Link to="/daily-drop">
                  <Button variant="outline" className="gap-2" data-testid="browse-btn">
                    <BookOpen className="w-4 h-4" />
                    Browse Content
                  </Button>
                </Link>
              </div>
            </div>

            {/* Suggested Content */}
            {suggestions.length > 0 && (
              <div className="border-t pt-10">
                <h2 className="font-serif text-xl font-semibold mb-6 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-gold" />
                  You Might Like
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {suggestions.map((item) => {
                    const Icon = getContentIcon(item.content_type);
                    return (
                      <Link 
                        key={item.content_id} 
                        to={getContentUrl(item)}
                        className="group"
                        data-testid={`suggestion-${item.content_id}`}
                      >
                        <Card className="bg-card border-none shadow-card card-hover h-full overflow-hidden">
                          <div className="aspect-video overflow-hidden relative">
                            <img
                              src={item.image_url || 'https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg'}
                              alt={item.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                            {item.content_type === 'video' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                                  <Play className="w-4 h-4 text-primary fill-current ml-0.5" />
                                </div>
                              </div>
                            )}
                          </div>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Icon className="w-3 h-3 text-gold" />
                              <span className="text-xs font-semibold uppercase tracking-wider text-gold">
                                {item.category}
                              </span>
                            </div>
                            <h3 className="font-semibold line-clamp-2 group-hover:text-gold transition-colors">
                              {item.title}
                            </h3>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="content-detail">
      <SEO 
        title={content?.title}
        description={content?.summary || content?.content?.substring(0, 160)}
        image={content?.image_url}
        url={`/content/${content?.slug || id}`}
        type={content?.content_type === 'video' ? 'video.other' : 'article'}
        keywords={content?.tags?.join(', ')}
        article={{
          author: content?.author || 'SkinUse',
          publishedTime: content?.created_at,
          modifiedTime: content?.updated_at,
          section: content?.category,
          tags: content?.tags
        }}
      />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to={getBackLink(content.content_type)} 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {getTypeLabel(content.content_type)}
          </Link>

          {/* Hero Image / Video */}
          <div className="aspect-video rounded-sm overflow-hidden mb-8 relative">
            {content.content_type === 'video' && content.video_url ? (
              <iframe
                src={content.video_url}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <img
                src={content.image_url || 'https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg'}
                alt={content.title}
                className="w-full h-full object-cover"
              />
            )}
            {content.content_type === 'video' && !content.video_url && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="w-8 h-8 text-primary fill-current ml-1" />
                </div>
              </div>
            )}
          </div>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
            <span className="text-gold font-semibold uppercase tracking-wider">{content.category}</span>
            <span className="flex items-center gap-1 text-muted-foreground">
              <Clock className="w-4 h-4" />
              {content.read_time} min {content.content_type === 'video' ? 'watch' : 'read'}
            </span>
            <span className="flex items-center gap-1 text-muted-foreground">
              <Eye className="w-4 h-4" />
              {content.views} views
            </span>
            {likeCount > 0 && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <Heart className="w-4 h-4 text-red-500 fill-current" />
                {likeCount} likes
              </span>
            )}
            {ratingCount > 0 && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <Star className="w-4 h-4 text-yellow-500 fill-current" />
                {avgRating} ({ratingCount})
              </span>
            )}
            {pointsEarned && (
              <span className="flex items-center gap-1 px-2 py-1 bg-gold/10 rounded-sm">
                <Sparkles className="w-4 h-4 text-gold" />
                <span className="font-semibold text-gold">+{content.points_reward} earned</span>
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 leading-tight">
            {content.title}
          </h1>

          {/* Summary */}
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            {content.summary}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-8">
            <Button 
              variant={liked ? "default" : "outline"} 
              className={`gap-2 ${liked ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
              onClick={handleLike}
              disabled={likeLoading}
              data-testid="like-btn"
            >
              <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
              {liked ? 'Liked' : 'Like'}
              {likeCount > 0 && <span className="ml-1">({likeCount})</span>}
            </Button>
            <Button 
              variant={bookmarked ? "default" : "outline"} 
              className={`gap-2 ${bookmarked ? 'bg-gold hover:bg-gold/90 text-black' : ''}`}
              onClick={handleBookmark}
              disabled={bookmarkLoading}
              data-testid="bookmark-btn"
            >
              {bookmarked ? (
                <BookmarkCheck className="w-4 h-4" />
              ) : (
                <Bookmark className="w-4 h-4" />
              )}
              {bookmarked ? 'Saved' : 'Save'}
              {bookmarkCount > 0 && <span className="ml-1">({bookmarkCount})</span>}
            </Button>
            <Button 
              variant="outline" 
              className="gap-2" 
              onClick={handleShare}
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>

          {/* Rating Section */}
          <div className="flex items-center gap-4 mb-12 p-4 bg-secondary/30 rounded-lg">
            <span className="text-sm font-medium">Rate this content:</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleRate(star)}
                  className="p-1 hover:scale-110 transition-transform"
                  data-testid={`rate-${star}`}
                >
                  <Star 
                    className={`w-6 h-6 ${
                      userRating && star <= userRating 
                        ? 'text-yellow-500 fill-current' 
                        : 'text-muted-foreground hover:text-yellow-500'
                    }`} 
                  />
                </button>
              ))}
            </div>
            {userRating && (
              <span className="text-sm text-muted-foreground">
                Your rating: {userRating}/5
              </span>
            )}
            {ratingCount > 0 && (
              <span className="text-sm text-muted-foreground ml-auto">
                Avg: {avgRating}/5 ({ratingCount} ratings)
              </span>
            )}
          </div>

          {/* Content Body */}
          <Card className="bg-card border-none shadow-card mb-12">
            <CardContent className="p-8 md:p-12">
              <div 
                className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-foreground prose-p:text-foreground prose-p:leading-relaxed prose-strong:text-foreground prose-em:text-foreground prose-li:text-foreground prose-a:text-gold prose-a:no-underline hover:prose-a:underline"
                dangerouslySetInnerHTML={{ __html: content.content }}
              />
            </CardContent>
          </Card>

          {/* Tags */}
          {content.tags?.length > 0 && (
            <div className="mb-12">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Related Topics</h3>
              <div className="flex flex-wrap gap-2">
                {content.tags.map((tag, i) => (
                  <span key={i} className="px-4 py-2 bg-secondary text-sm rounded-sm hover:bg-secondary/80 transition-colors cursor-pointer">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations Section */}
          {recommendations.length > 0 && (
            <div className="mb-12" data-testid="recommendations">
              <h3 className="font-serif text-2xl font-semibold mb-6">You Might Also Like</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {recommendations.map((rec) => {
                  const Icon = getContentIcon(rec.content_type);
                  return (
                    <Link key={rec.content_id} to={`/content/${rec.slug || rec.content_id}`}>
                      <Card className="bg-card border-none shadow-card h-full hover:shadow-lg transition-shadow group">
                        <div className="aspect-video overflow-hidden">
                          <img 
                            src={rec.image_url || 'https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg'} 
                            alt={rec.title} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                          />
                        </div>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className="w-3 h-3 text-gold" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-gold">{rec.category}</span>
                          </div>
                          <h4 className="font-semibold line-clamp-2 group-hover:text-gold transition-colors">{rec.title}</h4>
                          <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                            <span>{rec.read_time} min read</span>
                            <span className="flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-gold" />
                              +{rec.points_reward}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Knowledge Tips Section */}
          {knowledgeTips.length > 0 && (
            <div className="mb-12" data-testid="knowledge-tips">
              <div className="flex items-center gap-2 mb-6">
                <Lightbulb className="w-5 h-5 text-gold" />
                <h3 className="font-serif text-2xl font-semibold">Quick Tips</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {knowledgeTips.map((tip, index) => (
                  <Card key={index} className="bg-gold/5 border-gold/20 border">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                          <Lightbulb className="w-5 h-5 text-gold" />
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">{tip.title}</h4>
                          <p className="text-sm text-muted-foreground">{tip.content}</p>
                          <span className="inline-block mt-2 text-xs text-gold font-medium">{tip.category}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Quiz CTA Section */}
          <div className="mb-12" data-testid="quiz-cta">
            <Card className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground overflow-hidden">
              <CardContent className="p-8 relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gold/10 rounded-full translate-y-1/2 -translate-x-1/2" />
                <div className="relative flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center">
                      <HelpCircle className="w-8 h-8 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-semibold mb-1">Test Your Knowledge</h3>
                      <p className="text-primary-foreground/70 text-sm">Take our quiz and earn up to 100 SkinPoints!</p>
                    </div>
                  </div>
                  <Link to="/quiz">
                    <Button className="btn-gold whitespace-nowrap" data-testid="take-quiz-btn">
                      <Brain className="w-4 h-4 mr-2" />
                      Take the Quiz
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Related Videos Section */}
          {relatedVideos.length > 0 && (
            <div className="mb-12" data-testid="related-videos">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-gold" />
                  <h3 className="font-serif text-2xl font-semibold">Watch More</h3>
                </div>
                <Link to="/video-vault" className="text-sm text-gold hover:underline flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {relatedVideos.map((video) => (
                  <Link key={video.content_id} to={`/content/${video.slug || video.content_id}`} className="group">
                    <div className="aspect-video rounded-sm overflow-hidden relative mb-2">
                      <img 
                        src={video.image_url || 'https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg'} 
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-4 h-4 text-primary fill-current ml-0.5" />
                        </div>
                      </div>
                    </div>
                    <h4 className="text-sm font-medium line-clamp-2 group-hover:text-gold transition-colors">{video.title}</h4>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Featured Products Section */}
          {featuredProducts.length > 0 && (
            <div className="mb-12" data-testid="featured-products">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-gold" />
                  <h3 className="font-serif text-2xl font-semibold">Featured Products</h3>
                </div>
                <Link to="/products" className="text-sm text-gold hover:underline flex items-center gap-1">
                  Browse All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {featuredProducts.map((product, index) => (
                  <Card key={index} className="bg-card border-none shadow-card group cursor-pointer hover:shadow-lg transition-shadow">
                    <div className="aspect-square p-4 bg-white rounded-t-sm">
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <CardContent className="p-4">
                      <p className="text-xs text-gold font-medium uppercase tracking-wide mb-1">{product.brand}</p>
                      <h4 className="text-sm font-medium line-clamp-2">{product.name}</h4>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* More to Explore Section */}
          {moreArticles.length > 0 && (
            <div className="mb-12" data-testid="more-articles">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-gold" />
                  <h3 className="font-serif text-2xl font-semibold">More to Explore</h3>
                </div>
                <Link to="/deep-dives" className="text-sm text-gold hover:underline flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {moreArticles.slice(0, 6).map((article) => {
                  const Icon = getContentIcon(article.content_type);
                  return (
                    <Link key={article.content_id} to={`/content/${article.slug || article.content_id}`} className="group">
                      <Card className="bg-card border-none shadow-card h-full hover:shadow-lg transition-shadow overflow-hidden">
                        <div className="aspect-video overflow-hidden">
                          <img 
                            src={article.image_url || 'https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg'} 
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className="w-3 h-3 text-gold" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-gold">{article.category}</span>
                          </div>
                          <h4 className="font-semibold line-clamp-2 group-hover:text-gold transition-colors">{article.title}</h4>
                          {article.read_time && (
                            <p className="text-xs text-muted-foreground mt-2">{article.read_time} min read</p>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA */}
          {!user && (
            <Card className="bg-gold/10 border-none">
              <CardContent className="p-8 text-center">
                <Sparkles className="w-8 h-8 text-gold mx-auto mb-4" />
                <h3 className="font-serif text-xl font-semibold mb-2">Earn SkinPoints</h3>
                <p className="text-muted-foreground mb-6">Sign up to earn points and unlock exclusive content</p>
                <Link to="/register">
                  <Button className="btn-gold" data-testid="cta-register">Create Free Account</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
